import { type ReactElement } from 'react'
import { ButtonCancel, PermissionTooltip, TButton } from '@components'
import { usePreference, useUpdatePermission, useUpdatePreference, useTranslation } from '@front/model'
import { Group, Modal, Stack, Text } from '@mantine/core'
import { type WithCompanyId, type CompanyId } from '@terros/common'
import { useQueryState, parseAsBoolean } from 'nuqs'

export const VisibilityModal = ({ companyId }: WithCompanyId): ReactElement => {
  const { t } = useTranslation()
  const [open, setOpen] = useQueryState('visibilityRestrictions', parseAsBoolean)
  const { data: restrictionEnabled } = usePreference('enableVisibilityRestrictions', companyId)

  return (
    <>
      <TButton icon={restrictionEnabled ? 'visibility_off' : 'visibility'} variant='secondary' onClick={() => setOpen(true)}>
        {t('Visibility Restrictions')}
      </TButton>
      <Modal opened={open ?? false} size='xl' title={t('Visibility Restrictions')} onClose={() => setOpen(false)}>
        {restrictionEnabled ? (
          <DisableVisibilityRestrictions companyId={companyId} onClose={() => setOpen(false)} />
        ) : (
          <EnableVisibilityRestrictions companyId={companyId} onClose={() => setOpen(false)} />
        )}
      </Modal>
    </>
  )
}

const EnableVisibilityRestrictions = ({
  companyId,
  onClose,
}: {
  companyId: CompanyId
  onClose: () => void
}): ReactElement => {
  const { t } = useTranslation()
  const { update: updatePermission, isUpdating: isUpdatingPermission } = useUpdatePermission()
  const { update: updatePreference, isUpdating: isUpdatingPreference } = useUpdatePreference(
    'enableVisibilityRestrictions',
    companyId
  )

  const updateWithPermission = async (): Promise<void> => {
    await updatePreference(true)
    updatePermission({ entityId: companyId, permissionType: 'user:list', scope: 'withinRestrictions' })
    updatePermission({ entityId: companyId, permissionType: 'team:list', scope: 'withinRestrictions' })
    onClose()
  }

  const updatePref = async (): Promise<void> => {
    await updatePreference(true)
    onClose()
  }

  return (
    <Stack>
      <Text>
        {t('Visibility restrictions allow you to configure permissions to be contained within specific teams')}
        <br />
        {t('When enabled, you can set as team as having restricted visibility.')}
        <br />
        {t('You can then set certain permissions to be contained within the team that has restricted visibility.')}
        <br />
        {t('We recommend setting permissions to view teams and users to be within visibility restrictions.')}
      </Text>
      <Group justify='space-between'>
        <ButtonCancel onClick={onClose} />

        <PermissionTooltip.Group permissionType='company:update' targetId={companyId}>
          <TButton loading={isUpdatingPreference || isUpdatingPermission} variant='secondary' onClick={updatePref}>
            {t('Enable without configuring permissions')}
          </TButton>
          <TButton loading={isUpdatingPreference || isUpdatingPermission} onClick={updateWithPermission}>
            {t('Enable and configure permissions')}
          </TButton>
        </PermissionTooltip.Group>
      </Group>
    </Stack>
  )
}

const DisableVisibilityRestrictions = ({
  companyId,
  onClose,
}: {
  companyId: CompanyId
  onClose: () => void
}): ReactElement => {
  const { t } = useTranslation()
  const { update: updatePreference, isUpdating: isUpdatingPreference } = useUpdatePreference(
    'enableVisibilityRestrictions',
    companyId
  )

  const handleUpdate = async (): Promise<void> => {
    await updatePreference(false)
    onClose()
  }

  return (
    <Stack>
      <Text>
        {t('Visibility restrictions allow you to configure permissions to be contained within specific teams')}
        <br />
        {t(
          'Before disabling visibility restrictions, please ensure that all permissions are configured to be outside of visibility restrictions, and no teams have restricted visibility'
        )}
      </Text>
      <Group justify='space-between'>
        <ButtonCancel onClick={onClose} />
        <PermissionTooltip permissionType='company:update' targetId={companyId}>
          <TButton loading={isUpdatingPreference} onClick={handleUpdate}>
            {t('Disable Visibility Restrictions')}
          </TButton>
        </PermissionTooltip>
      </Group>
    </Stack>
  )
}
